import { useLayoutEffect, useRef, useState } from "react";

import type { RefObject } from "react";

interface UseOverflowTabsResult {
  /** Attach to the element whose available width bounds the visible tabs. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Attach to a hidden clone of every candidate tab, used to measure natural widths. */
  measureRef: RefObject<HTMLDivElement | null>;
  /** How many candidate items (from the start of the array) currently fit in the container. */
  visibleCount: number;
}

/**
 * Measures a hidden clone of `itemCount` tabs against `containerRef`'s
 * available width, and reports how many fit as a contiguous prefix — the
 * tail of the array is what the caller is expected to collapse. Fits by each
 * tab's rendered right edge relative to the first tab's left edge, not by
 * summing individual border-box widths, so inter-tab margins from the
 * mounted theme are picked up without hardcoding their value here.
 *
 * `alwaysReserveTrigger` reserves `triggerWidth` from the budget on every
 * pass, for a caller whose "More" trigger always renders. Otherwise the
 * hook fits at the full width first, and only re-fits with `triggerWidth`
 * reserved if that first pass already didn't fit every item — so the
 * trigger's own space is accounted for as soon as it turns out to be needed.
 *
 * `freeze`, while true, skips recalculation entirely. Use it to ignore a
 * resize a caller knows is incidental to its own UI (e.g. the transient
 * width change a scroll-locking menu can cause on a fixed-position
 * ancestor) rather than a genuine viewport change.
 */
const useOverflowTabs = (
  itemCount: number,
  triggerWidth: number,
  alwaysReserveTrigger: boolean,
  freeze = false,
): UseOverflowTabsResult => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return undefined;
    }

    const fitCount = (budget: number): number => {
      const tabs = Array.from(
        measure.querySelectorAll<HTMLElement>('[role="tab"]'),
      );
      if (tabs.length === 0) {
        return 0;
      }
      const left = tabs[0].getBoundingClientRect().left;

      let fittingCount = 0;
      for (const tab of tabs) {
        const widthSoFar = tab.getBoundingClientRect().right - left;
        if (widthSoFar > budget) break;
        fittingCount += 1;
      }
      return fittingCount;
    };

    const recalculate = (): void => {
      if (freeze) {
        return;
      }

      const available = container.clientWidth;

      if (alwaysReserveTrigger) {
        setVisibleCount(fitCount(available - triggerWidth));
        return;
      }

      const unreservedFit = fitCount(available);
      const triggerIsNeeded = unreservedFit < itemCount;
      setVisibleCount(
        triggerIsNeeded ? fitCount(available - triggerWidth) : unreservedFit,
      );
    };

    recalculate();
    const observer = new ResizeObserver(recalculate);
    observer.observe(container);
    return () => observer.disconnect();
  }, [itemCount, triggerWidth, alwaysReserveTrigger, freeze]);

  return { containerRef, measureRef, visibleCount };
};

export { useOverflowTabs };
